import React, { useState, useEffect } from 'react';
import {
    AppBar, Toolbar, Typography, Box, Button, IconButton,
    CircularProgress, Alert, Dialog, DialogTitle, DialogContent, DialogActions,
    Card, CardContent, Grid, Checkbox, Chip
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';

function StudentManageAppVolPage({ labProps, setIsShowAppVol, onSaved }) {
    const [catalogPackages, setCatalogPackages] = useState([]);
    const [selectedPackages, setSelectedPackages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isModified, setIsModified] = useState(false);
    const [statusResult, setStatusResult] = useState(null);
    const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
    const [hasSaved, setHasSaved] = useState(false);

    // Carica il catalogo AppVol approvato e le selezioni correnti
    useEffect(() => {
        const fetchCatalog = async () => {
            try {
                const response = await fetch(process.env.REACT_APP_URL_GET_PREDEFINED_APPVOL_CATALOG, {
                    credentials: 'include',
                });
                const data = await response.json();
                if (data.statusRequest === 'success') {
                    setCatalogPackages(data.packages || []);
                }
            } catch (error) {
                console.error('Errore nel caricamento del catalogo:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchCatalog();

        // Imposta le selezioni correnti dello studente
        if (labProps.personalAppVol && labProps.personalAppVol.length > 0) {
            setSelectedPackages(labProps.personalAppVol);
        }
    }, [labProps.personalAppVol]);

    const isPackageSelected = (pkg) => {
        return selectedPackages.some(
            s => String(s.sourceDetail?.applicationId) === String(pkg.sourceDetail?.applicationId)
        );
    };

    const MAX_APPS = 5;

    const handleTogglePackage = (pkg) => {
        const appId = String(pkg.sourceDetail?.applicationId);
        if (isPackageSelected(pkg)) {
            setSelectedPackages(prev => prev.filter(
                s => String(s.sourceDetail?.applicationId) !== appId
            ));
        } else {
            if (selectedPackages.length >= MAX_APPS) return;
            setSelectedPackages(prev => [...prev, pkg]);
        }
        setIsModified(true);
    };

    const goBack = () => {
        if (hasSaved && onSaved) {
            onSaved();
        }
        setIsShowAppVol(false);
    };

    const handleBack = () => {
        if (isModified) {
            setShowUnsavedDialog(true);
        } else {
            goBack();
        }
    };

    const handleSave = async () => {
        if (isSubmitting) return;
        setIsSubmitting(true);
        setStatusResult(null);

        try {
            const response = await fetch(process.env.REACT_APP_URL_STUDENT_MANAGE_APPVOL, {
                credentials: 'include',
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    idLab: labProps.idLab,
                    appVolPackages: selectedPackages,
                }),
            });
            const data = await response.json();
            setStatusResult(data);

            if (data.statusRequest === 'success') {
                setIsModified(false);
                setHasSaved(true);
            }
        } catch (error) {
            console.error('Errore nel salvataggio:', error);
            setStatusResult({ statusRequest: 'error', message: 'Errore nel salvataggio' });
        } finally {
            setTimeout(() => setIsSubmitting(false), 500);
        }
    };

    return (
        <React.Fragment>
            <AppBar position="sticky" color="default" elevation={1} sx={{ backgroundColor: '#fff' }}>
                <Toolbar sx={{ gap: 2 }}>
                    <IconButton edge="start" onClick={handleBack} disabled={isSubmitting}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="h6" noWrap sx={{ fontWeight: 'bold', color: '#a71e3b' }}>
                            Gestisci le tue App
                        </Typography>
                        <Typography variant="body2" color="textSecondary" noWrap>
                            {labProps.labName} &mdash; {labProps.baseImage?.name}
                        </Typography>
                    </Box>
                    {statusResult && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                            {statusResult.statusRequest === 'success' && (
                                <HourglassEmptyIcon sx={{ fontSize: '1rem', color: 'warning.main' }} />
                            )}
                            <Typography
                                variant="body2"
                                color={statusResult.statusRequest === 'success' ? 'warning.main' : 'error'}
                            >
                                {statusResult.statusRequest === 'success'
                                    ? 'Le app verranno assegnate a breve'
                                    : statusResult.message || 'Errore nell\'aggiornamento'}
                            </Typography>
                        </Box>
                    )}
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                        disabled={isSubmitting || !isModified}
                        onClick={handleSave}
                    >
                        {isSubmitting ? 'Salvando...' : 'Salva modifiche'}
                    </Button>
                </Toolbar>
            </AppBar>

            <Box sx={{ p: 3 }}>
                <Alert severity="info" sx={{ mb: 3 }}>
                    Seleziona fino a <strong>{MAX_APPS} applicazioni</strong> che vuoi utilizzare nel laboratorio <strong>{labProps.labName}</strong>.
                    Dopo il salvataggio, le app verranno assegnate automaticamente entro pochi minuti.
                </Alert>

                {labProps.appVolProvisioningStatus && labProps.appVolProvisioningStatus !== 'none' && (
                    <Alert
                        severity={
                            labProps.appVolProvisioningStatus === 'completed' ? 'success' :
                            labProps.appVolProvisioningStatus === 'error' ? 'error' : 'warning'
                        }
                        sx={{ mb: 3 }}
                        icon={labProps.appVolProvisioningStatus === 'queued' || labProps.appVolProvisioningStatus === 'processing'
                            ? <HourglassEmptyIcon /> : undefined}
                    >
                        {labProps.appVolProvisioningStatus === 'queued' && 'Le tue app sono in coda per l\'assegnazione...'}
                        {labProps.appVolProvisioningStatus === 'processing' && 'Assegnazione app in corso...'}
                        {labProps.appVolProvisioningStatus === 'completed' && 'Le tue app sono state assegnate con successo.'}
                        {labProps.appVolProvisioningStatus === 'error' && 'Si è verificato un errore nell\'assegnazione. Il sistema riproverà automaticamente.'}
                    </Alert>
                )}

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress sx={{ color: '#a71e3b' }} />
                    </Box>
                ) : catalogPackages.length === 0 ? (
                    <Alert severity="warning">Nessuna applicazione disponibile nel catalogo.</Alert>
                ) : (
                    <Grid container spacing={2}>
                        {catalogPackages.map((pkg, index) => {
                            const selected = isPackageSelected(pkg);
                            const disabled = !selected && selectedPackages.length >= MAX_APPS;
                            return (
                                <Grid item xs={12} sm={6} md={4} lg={3} key={pkg.sourceDetail?.applicationId || index}>
                                    <Card
                                        elevation={0}
                                        onClick={() => !disabled && handleTogglePackage(pkg)}
                                        sx={{
                                            border: '2px solid',
                                            borderColor: selected ? '#a71e3b' : '#e0e0e0',
                                            borderRadius: '12px',
                                            cursor: disabled ? 'not-allowed' : 'pointer',
                                            transition: 'all 0.2s ease',
                                            bgcolor: selected ? 'rgba(167, 30, 59, 0.03)' : '#fff',
                                            opacity: disabled ? 0.5 : 1,
                                            '&:hover': disabled ? {} : {
                                                borderColor: '#a71e3b',
                                                boxShadow: '0 2px 12px rgba(167, 30, 59, 0.15)',
                                            }
                                        }}
                                    >
                                        <CardContent sx={{ pb: 1 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                                <Checkbox
                                                    checked={selected}
                                                    disabled={disabled}
                                                    sx={{
                                                        p: 0, mt: 0.25,
                                                        color: '#ccc',
                                                        '&.Mui-checked': { color: '#a71e3b' },
                                                    }}
                                                />
                                                <Inventory2Icon sx={{ color: selected ? '#a71e3b' : '#999', fontSize: '1.2rem', mt: 0.25 }} />
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Typography
                                                        variant="body1"
                                                        sx={{
                                                            fontWeight: 600,
                                                            color: selected ? '#a71e3b' : '#333',
                                                            wordBreak: 'break-word',
                                                        }}
                                                    >
                                                        {pkg.label || pkg.value}
                                                    </Typography>
                                                    {pkg.version && (
                                                        <Typography variant="caption" color="textSecondary">
                                                            v{pkg.version}
                                                        </Typography>
                                                    )}
                                                    {pkg.description && (
                                                        <Typography
                                                            variant="body2"
                                                            color="textSecondary"
                                                            sx={{
                                                                mt: 0.5,
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                display: '-webkit-box',
                                                                WebkitLineClamp: 2,
                                                                WebkitBoxOrient: 'vertical',
                                                                fontSize: '0.8rem',
                                                                lineHeight: 1.4,
                                                            }}
                                                        >
                                                            {pkg.description}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            );
                        })}
                    </Grid>
                )}

                {selectedPackages.length > 0 && (
                    <Box sx={{ mt: 3, p: 2, bgcolor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e0e0e0' }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                            App selezionate ({selectedPackages.length}/{MAX_APPS}):
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {selectedPackages.map((pkg, i) => (
                                <Chip
                                    key={i}
                                    label={pkg.label || pkg.value}
                                    size="small"
                                    onDelete={() => handleTogglePackage(pkg)}
                                    sx={{ bgcolor: 'rgba(167, 30, 59, 0.1)', color: '#a71e3b' }}
                                />
                            ))}
                        </Box>
                    </Box>
                )}
            </Box>

            <Dialog open={showUnsavedDialog} onClose={() => setShowUnsavedDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Modifiche non salvate</DialogTitle>
                <DialogContent>
                    <Typography>
                        Hai effettuato modifiche che non sono state salvate.
                        Se esci senza salvare, le modifiche andranno perse.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowUnsavedDialog(false)} color="primary" variant="outlined">
                        Continua a modificare
                    </Button>
                    <Button onClick={() => { setShowUnsavedDialog(false); goBack(); }} color="error" variant="contained">
                        Abbandona modifiche
                    </Button>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
}

export default StudentManageAppVolPage;
